import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import useStoryCards from '../../hooks/useStoryCards'
import { PARENT_TESTIMONIALS } from '../../data/siteContent'

const PROGRAM_STYLES = {
  british: {
    badge: 'bg-primary text-white',
    ring: 'ring-primary/20',
    bar: 'bg-primary',
  },
  american: {
    badge: 'bg-accent text-white',
    ring: 'ring-accent/25',
    bar: 'bg-accent',
  },
  national: {
    badge: 'bg-secondary text-white',
    ring: 'ring-secondary/20',
    bar: 'bg-secondary',
  },
}

function getProgramLabel(item, t) {
  return item.program ? t(`programs.${item.program}.title`) : ''
}

function getFooterMeta(meta, programLabel) {
  if (!meta?.trim()) return null
  if (!programLabel) return meta
  if (meta.trim().toLowerCase() === programLabel.trim().toLowerCase()) return null
  return meta
}

function ParentSlide({ item, t }) {
  const style = PROGRAM_STYLES[item.program] || PROGRAM_STYLES.british
  const programLabel = getProgramLabel(item, t)
  const footerMeta = getFooterMeta(item.meta, programLabel)

  return (
    <div className="grid overflow-hidden lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-stretch">
      <div className="relative min-h-[280px] sm:min-h-[320px] lg:min-h-[400px]">
        <div className={`absolute inset-y-0 start-0 z-10 w-1.5 ${style.bar}`} aria-hidden />
        <img
          src={item.image}
          alt={item.name}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/60 via-primary/10 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-black/10" />
        {programLabel ? (
          <span
            className={`absolute bottom-4 start-4 z-10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider shadow-lg ${style.badge}`}
          >
            {programLabel}
          </span>
        ) : null}
      </div>

      <div className="flex min-h-full flex-col justify-center gap-6 px-6 py-8 sm:px-10 sm:py-10 lg:gap-8 lg:px-12 lg:py-10">
        <Quote className="h-9 w-9 shrink-0 text-accent/80 sm:h-10 sm:w-10" aria-hidden />
        <blockquote className="text-lg font-medium leading-relaxed text-primary sm:text-xl lg:text-[1.35rem] lg:leading-snug">
          <span className="text-accent">&ldquo;</span>
          {item.quote}
          <span className="text-accent">&rdquo;</span>
        </blockquote>

        <footer className="border-t border-border/60 pt-5">
          <p className="text-lg font-bold text-primary">{item.name}</p>
          {footerMeta ? (
            <p className="mt-1 text-sm font-medium text-text/55">{footerMeta}</p>
          ) : null}
        </footer>
      </div>
    </div>
  )
}

function buildFallbackSlides(t) {
  return PARENT_TESTIMONIALS.map((item) => ({
    id: item.key,
    image: item.image,
    program: item.program,
    name: t(`pagesExtras.parents.${item.key}.family`),
    meta: '',
    quote: t(`pagesExtras.parents.${item.key}.quote`),
  }))
}

export default function ParentTestimonialsPageContent() {
  const { t } = useTranslation()
  const { isRTL, localized } = useLanguage()
  const { items: apiItems } = useStoryCards('parents')
  const [index, setIndex] = useState(0)

  const slides = useMemo(() => {
    if (apiItems.length) {
      return apiItems.map((item) => ({
        id: item.id,
        image: item.image,
        program: item.program,
        name: localized(item, 'name'),
        meta: localized(item, 'meta'),
        quote: localized(item, 'quote'),
      }))
    }
    return buildFallbackSlides(t)
  }, [apiItems, localized, t])

  const total = slides.length
  const current = slides[index] || slides[0]

  const prev = () => setIndex((i) => (i - 1 + total) % total)
  const next = () => setIndex((i) => (i + 1) % total)

  if (!current) return null

  return (
    <div className="space-y-10">
      <div className="mx-auto max-w-3xl text-center">
        <p className="mb-3 text-sm font-bold uppercase tracking-widest text-accent">
          {t('parentTestimonialsPage.introLabel')}
        </p>
        <p className="text-lg leading-relaxed text-text/75">{t('parentTestimonialsPage.intro1')}</p>
        <p className="mt-4 text-lg leading-relaxed text-text/75">{t('parentTestimonialsPage.intro2')}</p>
      </div>

      <div className="overflow-hidden rounded-3xl border border-border/60 bg-background shadow-xl shadow-primary/5">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: isRTL ? -24 : 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? 24 : -24 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <ParentSlide item={current} t={t} />
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
          {slides.map((item, i) => {
            const style = PROGRAM_STYLES[item.program] || PROGRAM_STYLES.british
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-left text-xs transition-all ${
                  i === index
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border/60 bg-white hover:border-primary/30'
                }`}
                aria-label={item.name}
                aria-current={i === index ? 'true' : undefined}
              >
                <span
                  className={`h-7 w-7 shrink-0 overflow-hidden rounded-full ring-2 ${
                    i === index ? style.ring : 'ring-border/40'
                  }`}
                >
                  <img src={item.image} alt="" className="h-full w-full object-cover" />
                </span>
                <span className={`hidden font-semibold sm:inline ${i === index ? 'text-primary' : 'text-text/60'}`}>
                  {item.name}
                </span>
              </button>
            )
          })}
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={prev}
            className="rounded-full border border-border/60 bg-white p-2.5 text-primary transition-colors hover:border-primary/30 hover:bg-primary/5"
            aria-label={t('parentTestimonialsPage.previous')}
          >
            <ChevronLeft className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <span className="min-w-[3rem] text-center text-sm font-semibold text-text/50">
            {index + 1} / {total}
          </span>
          <button
            type="button"
            onClick={next}
            className="rounded-full border border-border/60 bg-white p-2.5 text-primary transition-colors hover:border-primary/30 hover:bg-primary/5"
            aria-label={t('parentTestimonialsPage.next')}
          >
            <ChevronRight className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  )
}
